import { Component, OnInit, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { map, Observable, tap } from 'rxjs';
import { EmployeeAndPositionSearchComponent } from 'src/app/components';
import { ReportType } from 'src/app/enums/report-type.enum';
import { IdName } from 'src/app/models/general/id-name.model';
import { PagedListResult } from 'src/app/models/paged-list-result.model';
import { PositionProfileColumn } from 'src/app/models/position-profile-column.model';
import { PositionProfile } from 'src/app/models/position-profile.model';
import { Position } from 'src/app/models/position.model';
import { QuestionCategoryResult } from 'src/app/models/question-category-result.model';
import { QuestionCategory } from 'src/app/models/question-category.model';
import { RatingBand } from 'src/app/models/rating-band.model';
import { ChartLegendItem, PersonLegend } from 'src/app/models/report/person-legend.model';
import { PositionProfileReportEmployees } from 'src/app/models/report/position-profile-report-employees.model';
import { EmployeePositionProfile, PositionReport, QuestionCategoryCell } from 'src/app/models/report/position-report.model';
import { ReportOptions } from 'src/app/models/report/report-options.model';
import { ReviewPeriodEmployee } from 'src/app/models/review-period-employee.model';
import { Search } from 'src/app/models/search.model';
import { AssessmentService, EmployeeService, PositionService, ReportService } from 'src/app/services';
import { ReviewPeriodEmployeeService } from 'src/app/services/review-period-employee.service';
import { TextHelper } from 'src/app/shared/helpers';
import { ArrayHelper } from 'src/app/shared/helpers/array.helper';
import { SearchConfig, SearchOption } from 'src/app/shared/models';

@UntilDestroy()
@Component({
  selector: 'app-position-profile-report',
  templateUrl: './position-profile-report.component.html',
  styleUrl: './position-profile-report.component.scss'
})
export class PositionProfileReportComponent implements OnInit {
  public reportData: PositionReport | null = null;
  public reportOptions: ReportOptions;
  public legend: PersonLegend[] = [];

  public positionId?: number;
  public catColumnPositionId?: number;

  public defaultReportTitleKey = 'POSITION_PROFILE_REPORT.POSITION_PROFILE';

  //loaded when selecting an employee
  public employeeProfiles: EmployeePositionProfile[] = [];

  public get orderedEmployeeProfiles(): EmployeePositionProfile[] {
    return this.getOrderedEmployees();
  }

  private mainCategories: QuestionCategory[] = [];
  public get filteredMainCategories(): QuestionCategory[] {
    return this.mainCategories.filter(cat => cat.isShowColumn);
  }
    
  public columns: PositionProfileColumn[] = [];
  public get filteredColumns(): PositionProfileColumn[] {
    return this.columns.filter(col => col.isShowColumn);
  }
  // show/hide columns
  public showEmployeeName = true;
  public showEmail = true;
  public showEmployeePosition = true;
  public showReviewDate = false;

  public showFitScore = true;
  public showTorzKatt = true;
  public showThinking = true;
  public showNoProfileCategories = true;

  public showDistributionTable = true;

  public listOrder: 'byAdd' | 'byName' | 'byFitScore' = 'byAdd';

  // eight colors for heatmap
  public colors = ['transparent', '#eff2f6', '#d3e5dc', '#b7d7c3', '#9bcaa9', '#7fbc90', '#63af76', '#47a15d', '#2B9443'];

  public ratingBands: RatingBand[] = [
    { nameKey: '', color: '#17a5e3', bandValueTextKey: 'POSITION_PROFILE_REPORT.GREATER_DIFFERENCE', bandDescriptionKey: '' },
    { nameKey: '', color: '#c7d1e6', bandValueTextKey: 'POSITION_PROFILE_REPORT.MINOR_DIFFERENCE', bandDescriptionKey: '' },
    { nameKey: '', color: '#eff2f6', bandValueTextKey: 'POSITION_PROFILE_REPORT.APPROPRIATE_FIT', bandDescriptionKey: ''  },
    { nameKey: '', color: 'transparent', bandValueTextKey: 'POSITION_PROFILE_REPORT.NOT_COMPARED_WITH_PROFILE', bandDescriptionKey: '', borderColor: '#efefef' }
  ];

  //distribution report
  //columns 1-8
  public distColumns = Array.from({ length: 8 }, (_, i) => i + 1);

  public getNumberHunSuffix = TextHelper.getNumberHunSuffix;

  @ViewChild('empSearch')
  empSearch: EmployeeAndPositionSearchComponent;

  getPositionList = (options?: Search): Observable<PagedListResult<Position>> => {
    options.viewMode = 'hasProfiles';
    return this.positionService.GetListForCommonAndCurrentCompany(options);
  }

  public positionSearchConfig: SearchConfig<Position, number> = {
    mapItem: (model: Position) => {
      return {
        id: model.id, 
        text: model.name,
        dropText: model.name,
        groupKey: model.groupKey,
        itemSource: model
      } as SearchOption<Position, number>;
    },
    getList: this.getPositionList
  };

  public searchEmployeeConfig: SearchConfig<IdName, number> = {
      mapItem: (model: IdName) => {
        return {
          id: model.id, 
          text: model.name,
          dropText: model.description,
          itemSource: model } as SearchOption<IdName, number>;
      },
      getList: this.assessmentService.getListForEmployee
  };

  constructor(
    private reportService: ReportService, 
    private positionService: PositionService, 
    private assessmentService: AssessmentService,
    private translate: TranslateService
  ) { 
    
  }

  ngOnInit() {
      this.translate.stream([
        'POSITION_PROFILE_REPORT.SZILUETT_ASSESSMENT',
        this.defaultReportTitleKey
      ]).subscribe(translations => {
        this.reportOptions = {
              title: translations[this.defaultReportTitleKey],
              subtitle: translations['POSITION_PROFILE_REPORT.SZILUETT_ASSESSMENT'],
              titleIndexText: '',
              logoLink: '/assets/img/logo-sziluett.png',
              showCurrentDate: true,
              reportType: ReportType.PositionProfile
          } as ReportOptions;
      });

      this.reportService.getPositionProfileReport()
      .pipe(untilDestroyed(this))
      .subscribe(data => {
        this.reportData = data;

        this.columns = this.getCategoryColumns(this.reportData.questionCategories);
        this.mainCategories = this.getMainCategoryCells();
      });

  }

  getSubCatCols(subCategories: QuestionCategoryCell[]): QuestionCategoryCell[] {
    return subCategories.filter(c => c.isShowColumn);
  }

  getCategoryColumns(mainCategories: QuestionCategory[]): PositionProfileColumn[] {
    
    let cols: PositionProfileColumn[] = [];
    let currentCol: PositionProfileColumn;

    mainCategories.forEach(mainCat => {
      mainCat.childCategories.forEach(cat => {
        currentCol = {
          name: cat.name,
          categoryId: cat.id,
          rightBorder: false,
          code: cat.code,
          min: cat.factorMin,
          max: cat.factorMax,
          mainCatName: mainCat.name,
          mainCatId: mainCat.id,
          isShowColumn: true,
          isKeyFactor: false
        } as PositionProfileColumn;

        cols.push(currentCol);
      });

      mainCat.isShowColumn = true;

      //every last column of the main category should have a right border
      currentCol.rightBorder = true;
    });

    return cols;
  }

  // isLastVisibleColumnOfCategory(col: QuestionCategoryCell): boolean {
  //   const catCols = this.filteredColumns.filter(c => c.mainCatName === col.);
  //   return catCols.indexOf(col) === catCols.length - 1;
  // }

  onPositionOptionClick(option: SearchOption<Position, number>) {

    if (!!(option as any).type) {
      //this.positionId = null;
      //this.reportOptions.title = this.defaultReportTitle;
      return;
    } 
    else
    {
      //console.info('onPositionOptionClick-option:', option);

      if (option.id)
      {
        this.positionId = option.id;
        this.reportOptions.title = option.text;
      }
      else
      {
        this.positionId = null;
        this.reportOptions.title = this.translate.instant(this.defaultReportTitleKey);
      }
    }

    this.updateList();
    //this.router.navigate([`/position/${option.id}`]);
  }

  public updateList() {
    if (!this.employeeProfiles) return;

    const rpList = this.employeeProfiles.map(ep => ep.reviewPeriodEmployeeId);

    this.reportService.getPositionProfileData(rpList, this.positionId)
    .pipe(
      map(result => {
        this.updateCategoryColumns(result.profile);
        return this.loadPositionProfileData(result.employees);
      }),
      untilDestroyed(this)
    )
    .subscribe(employees => {
      this.employeeProfiles = employees ?? [];
    });
  }

  public updateColumns() {
    this.columns = this.getFilteredColumns();
    this.mainCategories = this.getMainCategoryCells();
    this.employeeProfiles = this.loadPositionProfileData(this.employeeProfiles);
  }

  public getMainCategoryCells(): QuestionCategory[] {
    let cols = this.reportData.questionCategories;
    
    cols.forEach(cat => {
      cat.childCount = this.columns.filter(c => c.isShowColumn && c.mainCatName === cat.name).length;

      if (cat.childCount === 0) cat.isShowColumn = false;
      else cat.isShowColumn = true;
    });

    return cols;
  }


  // Az oszlopok megjelenítését szabályozó checkboxok alapján leszűrjük a kategória oszlopokat. Az összes oszlop közül csak azokat adjuk vissza melyek megjeleníthetőek.
  //  showTorzKatt false esetén code=CSAL és code=TORZ oszlopokat el kell rejteni
  //  showThinking false esetén code=MOT009 és code=MOT002 oszlopokat el kell rejteni
  //  showNoProfileCategories false esetén azokat az oszlopokat kell elrejteni, amelyeknek nincs min-max értéke
  public getFilteredColumns(): PositionProfileColumn[] {
    let cols = this.columns;

     cols.forEach(col => {
      if (!this.showTorzKatt && (col.code === 'CSAL' || col.code === 'TORZ')) col.isShowColumn = false;
      else if (!this.showThinking && (col.code === 'MOT009' || col.code === 'MOT002')) col.isShowColumn = false;
      else if (!this.showNoProfileCategories && (!col.min || !col.max)) col.isShowColumn = false;
      else col.isShowColumn = true;
    });

    return cols;
  }

  //Beállítja a kategória oszlopok min-max értékét a munkakörhöz tartozó értékek alapján, vagy nullázza azt ha nincs munkakör kiválasztva
  updateCategoryColumns(profile: PositionProfile[]) {

    //console.info('catColumnPositionId: [' + this.catColumnPositionId + '], positionId: [' + this.positionId + ']');

    if (this.catColumnPositionId === this.positionId) return;

    //console.info('updateCatgeoryColumns - update', profile);

    this.columns.forEach(col => {
      const profileData = profile ? profile.find(p => p.questionCategoryId === col.categoryId) : null;
      col.min = profileData?.factorMin;
      col.max = profileData?.factorMax;
      col.isKeyFactor = profileData?.isKeyFactor ?? false;
      
      if (col.isKeyFactor) {
        console.log(`Setting isKeyFactor true for column: ${col.categoryId} - ${col.name}`);
      }
    });

    //Jelezzük, hogy beállítottuk a munkakörhöz tartozó értékeket
    this.catColumnPositionId = this.positionId;
  }

  // get average for category in employeeProfiles, use catCols property, and the given column index
  public getAverageForCategory(columnIndex: number): number {
    let sum = 0;
    let count = 0;
    this.employeeProfiles.forEach(ep => {
      if (ep.catCols[columnIndex].value) {
        sum += ep.catCols[columnIndex].value;
        count++;
      }
    });

    return count > 0 ? sum / count : 0;
  }

  loadPositionProfileData(employees: EmployeePositionProfile[]): EmployeePositionProfile[] {
    
    if (!employees || employees?.length === 0) return [];

    //console.info('loadPositionProfileData', result.employees);

    employees.forEach(ep => {
      ep.catCols = ep.questionCategoryResults.map(q => {

        const col = this.columns.find(c => c.categoryId === q.categoryId);
        const colsOfMain = this.columns.filter(c => c.isShowColumn && c.mainCatName === col.mainCatName);
        const lastCol = colsOfMain.length > 0 ? colsOfMain[colsOfMain.length - 1] : null;

        return {
          value: q.result,
          color: this.getCellColor(q),
          categoryId: q.categoryId,
          isShowColumn: col.isShowColumn,
          rightBorder: lastCol?.categoryId === q.categoryId,
          isKeyFactor: col.isKeyFactor
        } as QuestionCategoryCell;
      });
    });

    return employees; 
  }

  public getLeaders(leaders: string[]): string {
    return leaders.join(', ');
  }

  public getCellColor(data: QuestionCategoryResult) {
    
    let color: string = 'initial';
    let cat = this.columns.find(c => c.categoryId === data.categoryId);
    if (data.result === 0 || cat === null || !cat.max || !cat.min) 
      return color;
    
    //benne van az intervallumban -> világos szürke
    if (cat.min <= data.result && data.result <= cat.max)
    {
      return '#eff2f6';
    }
    // egyel esik ki az intervallumból -> szürke
    else if (cat.min - 1 === data.result || data.result === cat.max + 1)
    {
      return '#c7d1e6';
    }
    //kettővel esik ki az intervallumból -> kék
    else if (cat.min - 1 > data.result || data.result > cat.max + 1)
    {
      return '#17a5e3';
    }

    return color; //todo colors
  }

  public addEmployee(rpeId: number) {
    const rpList = [rpeId];

    this.reportService.getPositionProfileData(rpList, this.positionId)
    .pipe(
      map(result => { 
        this.updateCategoryColumns(result.profile);
        return this.loadPositionProfileData(result.employees);
      }),
      untilDestroyed(this)
    )
    .subscribe(employees => {

      if (employees?.length > 0)
      {
        this.employeeProfiles.push(employees[0]);
      }
    });
      
  }  

  public getOrderedEmployees(): EmployeePositionProfile[] {

    if (this.listOrder === 'byName') {
      return ArrayHelper.sortByStringField([...this.employeeProfiles], 'employeeName');
    }
    else if (this.listOrder === 'byFitScore') {
      return ArrayHelper.sortByNumberFieldDesc([...this.employeeProfiles], 'fitScore');
    }
    else //'byAdd'
      return this.employeeProfiles
  }

  public deleteEmployee(rpeId: number) {
    this.employeeProfiles = this.employeeProfiles.filter(ep => ep.reviewPeriodEmployeeId !== rpeId);
    this.empSearch.removeItem(rpeId);
  }

  public onPositionCleared() {
    this.positionId = null;
    this.reportOptions.title = this.defaultReportTitleKey;
   
    this.updateList();
  }

  public getCategoryCss(cat: QuestionCategory) {
    let css = 'main-cat-' + cat.code;
    return css;
  }

  //counts the number of instances among the employees when the col.value is a given value
  public getCatCount(cat: QuestionCategory, value: number): number {
    let count = 0;

    if (!this.orderedEmployeeProfiles) return 0;

    this.orderedEmployeeProfiles.forEach(ep => {
      count += ep.catCols.filter(c => c.categoryId === cat.id && c.value === value).length;
    });

    return count;
  }

  public getCatAverage(cat: QuestionCategory): number {
    let sum = 0;
    let count = 0;

    if (!this.orderedEmployeeProfiles) return 0;

    this.orderedEmployeeProfiles.forEach(ep => {
      let col = ep.catCols.find(c => c.categoryId === cat.id);
      if (col && col.value) {
        sum += col.value;
        count++;
      }
    });

    return count > 0 ? sum / count : 0;
  }

  public isKeyFactor(categoryId: number): boolean {
    if (!this.columns) return false;
    const col = this.columns.find(x => x.categoryId === categoryId);
    return col ? col.isKeyFactor : false;
  }

  public getDistributionList() {
    return this.filteredMainCategories.filter(col => {
      if (!this.showTorzKatt && (col.code === 'CSAL' || col.code === 'TORZ')) return false;
      
      return true;
    });
  }

  public displayChildCats(childCats: QuestionCategory[]): QuestionCategory[] {
    return childCats.filter(x => {
        if (!this.showThinking && (x.code === 'MOT009' || x.code === 'MOT002')) return false;
        
        const col = this.columns.find(c => c.categoryId === x.id && c.isShowColumn);
        
        return !!col;
        // else if (!this.showNoProfileCategories && (!col.factorMin || !col.factorMax)) return false;
        // return true;
    });
  }

  getColor(value: number): string {
    return this.colors[value];
  }
}
